import type { TowerType } from "@mmotdg/shared";
import * as THREE from "three";
import { makeProjectileMaterial, wrapMesh } from "./core.js";
import type { ProjectileModelView, ProjectileSkin } from "./types.js";

function buildArcherArrow(): ProjectileModelView {
  const geo = new THREE.ConeGeometry(0.06, 0.32, 6);
  geo.rotateX(Math.PI / 2);
  const mesh = new THREE.Mesh(geo, makeProjectileMaterial("archer", { metalness: 0.5 }));
  return wrapMesh(mesh);
}

function buildCannonball(): ProjectileModelView {
  const geo = new THREE.SphereGeometry(0.14, 12, 10);
  const mesh = new THREE.Mesh(geo, makeProjectileMaterial("cannon", { metalness: 0.7 }));
  return wrapMesh(mesh);
}

function buildFrostShard(): ProjectileModelView {
  const geo = new THREE.OctahedronGeometry(0.13, 0);
  const mesh = new THREE.Mesh(geo, makeProjectileMaterial("frost", { metalness: 0.1 }));
  return wrapMesh(mesh);
}

function buildLightningBolt(): ProjectileModelView {
  const geo = new THREE.TetrahedronGeometry(0.16, 0);
  const mesh = new THREE.Mesh(geo, makeProjectileMaterial("lightning"));
  return wrapMesh(mesh);
}

function buildPoisonDrop(): ProjectileModelView {
  const geo = new THREE.IcosahedronGeometry(0.13, 0);
  const mesh = new THREE.Mesh(geo, makeProjectileMaterial("poison"));
  return wrapMesh(mesh);
}

function buildAntiAirMissile(): ProjectileModelView {
  const geo = new THREE.CylinderGeometry(0.05, 0.08, 0.32, 8);
  geo.rotateX(Math.PI / 2);
  const mesh = new THREE.Mesh(geo, makeProjectileMaterial("antiair", { metalness: 0.6 }));
  return wrapMesh(mesh);
}

function buildSupportSpark(): ProjectileModelView {
  const geo = new THREE.SphereGeometry(0.1, 8, 6);
  const mesh = new THREE.Mesh(geo, makeProjectileMaterial("support"));
  return wrapMesh(mesh);
}

function buildEconomyCoin(): ProjectileModelView {
  const geo = new THREE.CylinderGeometry(0.13, 0.13, 0.04, 16);
  geo.rotateX(Math.PI / 2);
  const mesh = new THREE.Mesh(geo, makeProjectileMaterial("economy", { metalness: 0.8 }));
  return wrapMesh(mesh);
}

const BUILDERS: Record<TowerType, () => ProjectileModelView> = {
  archer: buildArcherArrow,
  cannon: buildCannonball,
  frost: buildFrostShard,
  lightning: buildLightningBolt,
  poison: buildPoisonDrop,
  support: buildSupportSpark,
  antiair: buildAntiAirMissile,
  economy: buildEconomyCoin,
};

const DISPLAY_NAMES: Record<TowerType, string> = {
  archer: "Arrow",
  cannon: "Cannonball",
  frost: "Frost Shard",
  lightning: "Lightning Bolt",
  poison: "Poison Drop",
  support: "Support Spark",
  antiair: "Missile",
  economy: "Coin",
};

function makeDefaultSkin(type: TowerType): ProjectileSkin {
  return {
    id: "default",
    towerType: type,
    displayName: DISPLAY_NAMES[type],
    build: BUILDERS[type],
  };
}

export const defaultProjectileSkins: Record<TowerType, ProjectileSkin> = {
  archer: makeDefaultSkin("archer"),
  cannon: makeDefaultSkin("cannon"),
  frost: makeDefaultSkin("frost"),
  lightning: makeDefaultSkin("lightning"),
  poison: makeDefaultSkin("poison"),
  support: makeDefaultSkin("support"),
  antiair: makeDefaultSkin("antiair"),
  economy: makeDefaultSkin("economy"),
};
